import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box,
  Alert
} from '@mui/material';
import { AccessTime, ExitToApp } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const SessionWarningDialog = () => {
  const [open, setOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const { logout } = useAuth();

  useEffect(() => {
    const handleSessionWarning = (event) => {
      setTimeRemaining(event.detail.timeRemaining);
      setOpen(true);
    };

    const handleSessionExpired = () => {
      setOpen(false);
    };

    const handleWarningDismissed = () => {
      setOpen(false);
    };

    // Écouter les événements de session
    window.addEventListener('sessionWarning', handleSessionWarning);
    window.addEventListener('sessionExpired', handleSessionExpired);
    window.addEventListener('sessionWarningDismissed', handleWarningDismissed);

    return () => {
      window.removeEventListener('sessionWarning', handleSessionWarning);
      window.removeEventListener('sessionExpired', handleSessionExpired);
      window.removeEventListener('sessionWarningDismissed', handleWarningDismissed);
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!open || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1000;
        if (newTime <= 0) {
          setOpen(false);
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, timeRemaining]);

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / (1000 * 60));
    const seconds = Math.floor((ms % (1000 * 60)) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleExtendSession = () => {
    window.dispatchEvent(new CustomEvent('sessionWarningDismissed'));
    setOpen(false);
  };

  const handleLogoutNow = () => {
    setOpen(false);
    logout();
  };

  const progressValue = timeRemaining > 0 ? (timeRemaining / (5 * 60 * 1000)) * 100 : 0;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        pb: 1
      }}>
        <AccessTime color="warning" />
        <Typography variant="h6" component="span">
          Session expire bientôt
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Votre session va expirer par inactivité dans :
          </Typography>
        </Alert>

        <Box sx={{ textAlign: 'center', my: 3 }}>
          <Typography 
            variant="h3" 
            color="warning.main"
            sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
          >
            {formatTime(timeRemaining)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            minutes : secondes
          </Typography>
        </Box>

        <LinearProgress 
          variant="determinate" 
          value={progressValue}
          color="warning"
          sx={{ 
            height: 8, 
            borderRadius: 4,
            mb: 2 
          }}
        />

        <Typography variant="body2" color="text.secondary" align="center">
          Cliquez sur "Continuer" pour prolonger votre session ou "Se déconnecter" pour fermer la session maintenant.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleLogoutNow}
          variant="outlined"
          color="error"
          startIcon={<ExitToApp />}
          sx={{ minWidth: 140 }}
        >
          Se déconnecter
        </Button>
        <Button
          onClick={handleExtendSession}
          variant="contained"
          color="primary"
          sx={{ minWidth: 140 }}
        >
          Continuer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionWarningDialog;